import { useMemo } from 'react';
import React from 'react';
import { FIELD_TYPES, type Column, type TagOption } from '@tw/tw-component-library';
import { Heading } from '@tw/tw-component-library/common-wt';
import {
  WarningText,
} from '../../../../common/CustomTextDisplay';
import { SectionKey } from '../../../../../types';
import {
  EnhancedFieldConfig,
  EnhancedSection,
  FormSchemaConfig,
} from '../../types/form/schema.types';
import { FormState } from '../../types';
import { validateRequired } from '../Utils';
import {
  createShowWhenEqualsDependency,
  createStandardDisabledState,
} from '../shared';
import {
  ADDITIONAL_CLIENT_DETAILS_VALUE,
  GIFT_HOSPITALITY_DONATION_SECTION_TITLES,
  INDIVIDUAL_CLIENT_TOOLTIP,
  PARTY_DETAILS_ADD_MORE_KEY,
  PARTY_DETAILS_GRID_KEY,
} from './constants';
import {
  applyPartyModalFormState,
  formatPartyDetailsDisplayCell,
  getPartyTableMaxRows,
  GIVEN_OR_RECEIVED_BY_OPTIONS,
  PARTY_DISPLAY_COLUMNS,
  readPartyDetailRows,
  type PartyKind,
} from './partyDetails';
import { requiresGiftHospitalityAuthoriser } from './authoriserRules';
import {
  useCreatorPersonTag,
  useSeedCreatorPersonTag,
} from './useCreatorPersonTag';

const TAG_PICKER_EXTRAS = {
  tagPicker: {
    minSearchLength: 4,
    maxTags: 99,
    createNewTags: false,
  },
};

const ACCORDION_EXTRAS = {
  grouped: true,
  evenSpacing: true,
  defaultExpanded: true,
};

const createFieldSection = (
  title: string,
  fields: EnhancedFieldConfig[],
): EnhancedSection => ({
  title: { text: title },
  layout: 'single',
  fields,
  extras: ACCORDION_EXTRAS,
});

const createAdditionalClientDetailsModalField = (
  isDisabled: boolean,
): Record<string, unknown> => ({
  type: 'select',
  name: 'additionalClientDetailsRequired',
  label: 'Additional Client Details Required',
  required: true,
  disabled: isDisabled,
  hidden: true,
  options: [
    { label: 'No', value: 'no', disabled: false },
    {
      label: 'Yes',
      value: ADDITIONAL_CLIENT_DETAILS_VALUE,
      disabled: false,
    },
  ],
});

const PARTY_DETAIL_FIELDS: Record<
  PartyKind,
  Array<Record<string, unknown>>
> = {
  existingClient: [
    {
      name: 'existingClientNumber',
      label: 'Client name',
      type: 'tagPicker',
      required: true,
      hidden: true,
      extras: TAG_PICKER_EXTRAS,
      validate: (value: unknown): string | undefined =>
        validateRequired(value, 'Client name'),
    },
    {
      name: 'existingClientContactName',
      label: 'Name of the individual(s) at the client',
      type: FIELD_TYPES.Text,
      tooltip: INDIVIDUAL_CLIENT_TOOLTIP,
      required: false,
      hidden: true,
    },
  ],
  prospectiveClient: [
    {
      name: 'prospectiveClientName',
      label: 'Prospective client name',
      type: FIELD_TYPES.Text,
      required: true,
      hidden: true,
      validate: (value: unknown): string | undefined =>
        validateRequired(value, 'Prospective client name'),
    },
    {
      name: 'prospectiveClientContactName',
      label: 'Name of the individual(s) at the prospective client',
      type: FIELD_TYPES.Text,
      tooltip: INDIVIDUAL_CLIENT_TOOLTIP,
      required: false,
      hidden: true,
    },
  ],
  thirdParty: [
    {
      name: 'thirdPartyName',
      label: 'Name of third party/supplier',
      type: FIELD_TYPES.Text,
      required: true,
      hidden: true,
      validate: (value: unknown): string | undefined =>
        validateRequired(value, 'Name of third party/supplier'),
    },
    {
      name: 'thirdPartyContact',
      label: 'Name of the individual(s) at the third party/supplier',
      type: FIELD_TYPES.Text,
      tooltip: INDIVIDUAL_CLIENT_TOOLTIP,
      required: false,
      hidden: true,
    },
    {
      name: 'thirdPartyRelationship',
      label: 'Description of the relationship',
      type: FIELD_TYPES.Text,
      required: false,
      hidden: true,
    },
  ],
};

const PARTY_TABLE_COLUMNS: Column<Record<string, unknown>>[] = [
  {
    key: PARTY_DISPLAY_COLUMNS.type,
    header: 'Type',
    sortable: true,
    type: FIELD_TYPES.Text,
  },
  {
    key: PARTY_DISPLAY_COLUMNS.name,
    header: 'Name',
    sortable: true,
    type: FIELD_TYPES.Text,
  },
  {
    key: PARTY_DISPLAY_COLUMNS.individuals,
    header: 'Name of individual(s)',
    sortable: true,
    type: FIELD_TYPES.Text,
  },
  {
    key: PARTY_DISPLAY_COLUMNS.relationship,
    header: 'Description of relationship',
    sortable: true,
    type: FIELD_TYPES.Text,
  },
];

const createPartyDetailsTitle = (): EnhancedFieldConfig => ({
  type: 'customComponent',
  name: 'partyDetailsTitle',
  label: '',
  customComponent:  React.createElement(Heading, { level: 6, children: "Party Details" })
});

const createPartyDetailsTable = (
  isDisabled: boolean,
  rows: Record<string, unknown>[],
  maxRows: number,
  /**
   * TableField spreads modal draft fields onto the parent form. When that
   * triggers a schema rebuild mid-modal, re-apply visibility from this value
   * so party fields (and the additional-details question) are not wiped.
   */
  modalPartyType?: unknown,
): EnhancedFieldConfig => {
  const tableFormSchema = {
    useFormSchema: true,
    formSchema: {
      sections: [
        {
          title: {
            text: 'Add Details',
            type: 'h2',
          },
          fields: [
            {
              type: 'select',
              name: 'givenOrReceivedBy',
              label: 'Who was this given or received by?',
              required: true,
              disabled: isDisabled,
              options: [...GIVEN_OR_RECEIVED_BY_OPTIONS],
              validate: (value: unknown): string | undefined =>
                validateRequired(value, 'Who was this given or received by?'),
            },
            ...PARTY_DETAIL_FIELDS.existingClient.map((field) => ({
              ...field,
              disabled: isDisabled,
            })),
            ...PARTY_DETAIL_FIELDS.prospectiveClient.map((field) => ({
              ...field,
              disabled: isDisabled,
            })),
            ...PARTY_DETAIL_FIELDS.thirdParty.map((field) => ({
              ...field,
              disabled: isDisabled,
            })),
            createAdditionalClientDetailsModalField(isDisabled),
          ],
        },
      ],
    },
  };

  applyPartyModalFormState(tableFormSchema, modalPartyType);

  return {
    type: FIELD_TYPES.Table,
    name: PARTY_DETAILS_GRID_KEY,
    label: 'Given / Received by details',
    required: false,
    disabled: isDisabled,
    extras: {
      table: {
        data: rows,
        columns: PARTY_TABLE_COLUMNS,
        enableSort: true,
        // Add entry follows partyDetailsAddMore via maxRows (Yes → 7, No → current count).
        enableAddRow: !isDisabled && rows.length < maxRows,
        enableEdit: true,
        enableDelete: true,
        enableSearch: false,
        enableFilters: false,
        showRowNumbers: true,
        disabled: isDisabled,
        maxRows,
        customCellRenderer: (
          _value: unknown,
          columnKey: string | number | symbol,
          row: Record<string, unknown>
        ) => formatPartyDetailsDisplayCell(String(columnKey), row),
        onFormSchemaChange: (formData: Record<string, unknown>) => {
          applyPartyModalFormState(
            tableFormSchema,
            formData.givenOrReceivedBy,
          );
        },
        tableFormSchema: tableFormSchema as never,
      },
    },
  };
};

export const buildGiftHospitalityDonationRequestDetailsSchema = (
  formState: FormState,
  sectionKey: SectionKey,
  creatorTag: TagOption | null = null,
): FormSchemaConfig => {
    const isDisabled = createStandardDisabledState(formState, sectionKey);

    const savedPerson = formState.data?.givenReceivedByPerson;
    const partyRows = readPartyDetailRows(formState.data);

    const openingFields: EnhancedFieldConfig[] = [
      {
        type: 'select',
        name: 'registerType',
        label: 'What are you registering?',
        required: true,
        disabled: isDisabled,
        options: [
          { label: 'Gift', value: 'gift', disabled: false },
          {
            label: 'Hospitality/Entertainment',
            value: 'hospitalityEntertainment',
            disabled: false,
          },
          {
            label: 'Charitable Donation',
            value: 'charitableDonation',
            disabled: false,
          },
        ],
        validate: (value: unknown): string | undefined =>
          validateRequired(value, 'What are you registering?'),
      },
      {
        type: 'radio',
        name: 'givenReceived',
        label: 'Has this been given or received by TW?',
        required: true,
        disabled: isDisabled,
        options: [
          { label: 'Given', value: 'given', disabled: false },
          { label: 'Received', value: 'received', disabled: false },
        ],
        validate: (value: unknown): string | undefined =>
          validateRequired(value, 'Has this been given or received by TW?'),
      },
      {
        type: 'date',
        name: 'date',
        label: 'Date given/received',
        required: true,
        disabled: isDisabled,
        validate: (value: unknown): string | undefined =>
          validateRequired(value, 'Date given/received'),
      },
      {
        type: 'tagPicker',
        name: 'givenReceivedByPerson',
        label: 'Person at TW',
        required: true,
        disabled: isDisabled,
        extras: TAG_PICKER_EXTRAS,
        tooltip:
          "Where you are registering this on behalf of someone else, please provide the other person's name. Multiple individuals can be added where appropriate.",
        validate: (value: unknown): string | undefined =>
          validateRequired(value, 'Person at TW'),
        ...(savedPerson === undefined && creatorTag
          ? { defaultValue: [creatorTag] }
          : {}),
      },
      createPartyDetailsTitle(),
      createPartyDetailsTable(
        isDisabled,
        partyRows,
        getPartyTableMaxRows(
          partyRows,
          formState.data?.additionalClientDetailsRequired,
          formState.data?.[PARTY_DETAILS_ADD_MORE_KEY],
        ),
        formState.data?.givenOrReceivedBy,
      ),
    ];

    const closingFields: EnhancedFieldConfig[] = [
      {
        type: 'radio',
        name: 'pitchorTenderInProgress',
        label:
          'To the best of your knowledge, are we currently in (or about to enter into) a pitch/tender process or in the process of contract renewal/renegotiations with the client or other third party?',
        required: true,
        disabled: isDisabled,
        options: [
          { label: 'Yes', value: 'yes', disabled: false },
          { label: 'No', value: 'no', disabled: false },
        ],
      },
      {
        type: 'customComponent',
        name: 'instruction',
        label: '',
        customComponent: React.createElement(WarningText, {
          text: "In accordance with the Firm's Anti-Bribery Policy, no gifts, hospitality or charitable donations may be given or accepted during a tender process or contract negotiations.  If you are aware that the Firm is currently in a pitch/tender process or in the process of contract renewal/renegotiations, please contact antibribery@taylorwessing.com for further guidance.",
          size: 'small',
          showIcon: true,
        }),
        dependencies: [
          createShowWhenEqualsDependency('pitchorTenderInProgress', 'yes'),
        ] as EnhancedFieldConfig['dependencies'],
      },
      {
        type: 'radio',
        name: 'discountOffered',
        label: 'Has any time been written off or discount been offered?',
        required: true,
        disabled: isDisabled,
        options: [
          { label: 'Yes', value: 'yes', disabled: false },
          { label: 'No', value: 'no', disabled: false },
        ],
        validate: (value: unknown): string | undefined => {
          if (value === 'yes') {
            return validateRequired(
              value,
              'Value written off or discount offered (in GBP)',
            );
          }
          return undefined;
        },
      },
      {
        type: 'number',
        name: 'discountValue',
        label: 'Value written off or discount offered (in GBP)',
        required: true,
        disabled: isDisabled,
        dependencies: [
          createShowWhenEqualsDependency('discountOffered', 'yes'),
        ] as EnhancedFieldConfig['dependencies'],
      },
      {
        type: 'text',
        name: 'chairtyName',
        label: 'Name of recipient charity',
        required: true,
        disabled: isDisabled,
        dependencies: [
          createShowWhenEqualsDependency('registerType', 'charitableDonation'),
        ] as EnhancedFieldConfig['dependencies'],
      },
      {
        type: 'textArea',
        name: 'description',
        label: 'Description of gift/hospitality/donation',
        required: true,
        disabled: isDisabled,
      },
      {
        type: 'text',
        name: 'reason',
        label: 'Reason',
        required: true,
        disabled: isDisabled,
        tooltip:
          'Please provide the rationale for offering or accepting the gift/hospitality/donation.  For example, this may be to celebrate a special occasion (e.g. a birthday, Christmas, or completion of a deal), a specific business development initiative, or in the course of maintaining an ongoing relationship.',
      },
      {
        type: 'number',
        name: 'estimatedValue',
        label: 'Estimated or actual value (in GBP)',
        required: true,
        disabled: isDisabled,
        tooltip:
          'Where a gift/hospitality is given to multiple recipients from the same client or third party, please provide the total combined value.',
      },
      {
        type: 'radio',
        name: 'donationApproval',
        label:
          'I confirm that the donation has been approved by the Charities Committee',
        required: true,
        disabled: isDisabled,
        options: [
          { label: 'Yes', value: 'yes', disabled: false },
          { label: 'No', value: 'no', disabled: false },
        ],
        tooltip:
          'All charitable donations made by or on behalf of Taylor Wessing must be approved by the Charities Committee.  Please contact the Corporate Social Responsibility Manager/Head of the Charities Committee to obtain approval.',
        dependencies: [
          createShowWhenEqualsDependency(
            'registerType',
            'charitableDonation',
          ) && createShowWhenEqualsDependency('givenReceived', 'given'),
        ] as EnhancedFieldConfig['dependencies'],
      },
      ...(requiresGiftHospitalityAuthoriser(formState.data)
        ? [
            {
              type: 'tagPicker' as const,
              name: 'authoriser',
              label: 'Name of the authoriser',
              required: true,
              disabled: isDisabled,
              tooltip:
                'Please note that you will need to obtain prior approval from your practice area leader/business service director for gifts/hospitality of this value. If you have not obtained approval, please do so before submitting the form or contact antibribery@taylorwessing.com.',
              extras: TAG_PICKER_EXTRAS,
            },
          ]
        : []),
    ];

    return {
      sections: [
        {
          layout: 'auto',
          isGrouped: true,
          isGroupedAccordion: true,
          sections: [
            createFieldSection(
              GIFT_HOSPITALITY_DONATION_SECTION_TITLES.requestDetails,
              openingFields,
            ),
            createFieldSection(
              GIFT_HOSPITALITY_DONATION_SECTION_TITLES.giftHospitalityDonationDetails,
              closingFields,
            ),
          ],
        },
      ],
      order: {
        priority: 1,
      },
    };
};

export const useGiftHospitalityDonationRequestDetailsSchema = (
  formState: FormState,
): FormSchemaConfig => {
  const creatorTag = useCreatorPersonTag(formState);
  useSeedCreatorPersonTag(formState, creatorTag);

  return useMemo(
    () =>
      buildGiftHospitalityDonationRequestDetailsSchema(
        formState,
        'gift_hospitality_donation_uk_request_details',
        creatorTag,
      ),
    [creatorTag, formState],
  );
};
